import React from 'react';

import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';

import { Loading } from 'Components/Partials';

import PollsApi from 'Services/Api/Admin/MainStage/Polls';

import { Socket } from 'Services';

ChartJS.register(...registerables);

const modernColors = [
    '#34df83',
    '#d57fff',
    '#ffa56d',
    '#29a1e0',
];

const options = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
        legend: {
            display: false,
        },
        datalabels: {
            anchor: 'end',
            align: 'right',
            offset: 12,
            color: '#ffffff',
            font: {
                weight: 'bold',
                size: 16,
            },
            formatter: (value) => (value * 100).toFixed(0) + '%',
        },
    },
    scales: {
        y: {
            grid: {
                display: false,
                drawBorder: false,
            },
            border: {
                color: '#ffffff', // Make y-axis line white
            },
            ticks: {
                color: '#ffffff',
                font: {
                    size: 18,
                    family: 'Montserrat, sans-serif',
                    weight: 'bold',
                },
                padding: 10,
            },
        },
        x: {
            grid: {
                display: false,
                drawBorder: false,
            },
            border: {
                color: '#ffffff', // Make x-axis line white
            },
            max: 1,
            ticks: {
                color: '#ffffff',
                font: {
                    size: 14,
                    family: 'Montserrat, sans-serif',
                },
                callback: function (value) {
                    return value.toLocaleString('en-UK', { style: 'percent' });
                },
                stepSize: 0.2,
            },
        },
    },
    animation: {
        duration: 800,
        easing: 'easeOutQuart',
    },
    elements: {
        bar: {
            borderRadius: { topRight: 12, bottomRight: 12, topLeft: 0, bottomLeft: 0 },
            // removed global backgroundColor here so dataset-level colours are used
            borderSkipped: false,
            borderWidth: 0,
        },
    },
    layout: {
        padding: {
            left: 10,
            right: 30,
            top: 10,
            bottom: 10,
        },
    },
    color: '#ffffff',
    maintainAspectRatio: false,
    aspectRatio: 2.5,
};

export default class PollResultsGraph extends React.Component {
    /**
     * @var bgs
     * @type {array}
     */
    bgs = [
        'bg-app-blue-dark',
        'bg-app-red',
        'bg-app-orange',
        'bg-app-green',
    ];

    /**
     * @var colors
     * @type {array}
     */
    colors = [
        '#34df83',
        '#d57fff',
        '#ffa56d',
        '#29a1e0',
    ];

    /**
     * @var state
     */
    state = {
        loading: true,
        poll: null
    };

    /**
     * @method componentDidMount
     */
    componentDidMount() {
        this.handleOpenPoll(this.props.poll_id);
    };

    /**
     * @method componentDidUpdate
     * @param prevProps
     */
    componentDidUpdate = async (prevProps) => {
        if (this.props.poll_id !== prevProps.poll_id) {
            this.handleClosePoll(prevProps.poll_id);

            this.handleOpenPoll(this.props.poll_id);
        }
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        this.handleClosePoll(this.props.poll_id);
    };

    /**
     * @method handleOpenPoll
     * @params {poll_id}
     */
    handleOpenPoll = async (poll_id) => {
        if (poll_id) {
            this.setState({ loading: true });

            const request = await PollsApi.get({
                session: this.props.session_id,
                poll: poll_id
            });

            if (request.success) {
                this.setState({
                    loading: false,
                    poll: request.data.data
                });
            }

            Socket.getConnection()
                .private(`main_stage_sessions.polls.${poll_id}`)
                .listen('MainStagePollAnswered', this.handleUpdatePoll);
        }
    }

    /**
     * @method handleClosePoll
     * @params {poll_id}
     */
    handleClosePoll = (poll_id) => {
        if (poll_id) {
            Socket.getConnection()
                .leave(`main_stage_sessions.polls.${poll_id}`);
        }
    }

    /**
     * @method handleUpdatePoll
     */
    handleUpdatePoll = (e) => {
        const poll = this.state.poll;

        if (!poll || !e) {
            return;
        }

        // Ignore events for other polls
        if (poll.id !== e.poll_id) {
            return;
        }

        // Ranking-type event: e.ranking is an array of answer ids in ranked order
        if (Array.isArray(e.ranking) && e.ranking.length > 0) {
            const numAnswers = poll.answers.length || 0;

            // Update overall submission count if provided by the event
            if (typeof e.poll_submission_count === 'number') {
                poll.submission_count = e.poll_submission_count;
            }

            // Ensure answers have numeric points and position_counts array
            poll.answers.forEach((a) => {
                a.points = Number(a.points || 0);

                // Normalize position_counts into an array of length numAnswers
                if (!Array.isArray(a.position_counts)) {
                    if (a.position_counts && typeof a.position_counts === 'object') {
                        const arr = Array(numAnswers).fill(0);
                        for (let k = 0; k < numAnswers; k++) {
                            const key1 = (k + 1).toString();
                            arr[k] = Number(a.position_counts[key1] ?? a.position_counts[k] ?? 0);
                        }
                        a.position_counts = arr;
                    } else {
                        a.position_counts = Array(numAnswers).fill(Number(a.position_counts || 0));
                    }
                } else {
                    // ensure numeric entries and correct length
                    a.position_counts = a.position_counts.slice(0, numAnswers).map(v => Number(v || 0));
                    while (a.position_counts.length < numAnswers) a.position_counts.push(0);
                }
            });

            // Map id => index for quick lookup
            const idToIndex = {};
            poll.answers.forEach((a, i) => { idToIndex[a.id] = i; });

            // For each ranked answer, add points and increment position count
            e.ranking.forEach((answerId, pos) => {
                const idx = idToIndex[answerId];
                if (typeof idx !== 'undefined') {
                    // Points for a position: top = numAnswers, next = numAnswers - 1, ...
                    const pointsToAdd = Math.max(0, numAnswers - pos);
                    poll.answers[idx].points = Number(poll.answers[idx].points || 0) + pointsToAdd;
                    poll.answers[idx].position_counts[pos] = Number(poll.answers[idx].position_counts[pos] || 0) + 1;
                }
            });

            // Trigger update
            this.setState({ poll });
            return;
        }

        // Non-ranking (single answer) event: update specific answer submission_count
        if (e.answer_id) {
            const index = poll.answers.findIndex(answer => e.answer_id === answer.id);

            if (index !== -1) {
                // prefer event-provided counts when present
                if (typeof e.answer_submission_count === 'number') {
                    poll.answers[index].submission_count = e.answer_submission_count;
                }

                if (typeof e.poll_submission_count === 'number') {
                    poll.submission_count = e.poll_submission_count;
                }

                this.setState({ poll });
            }
        }
    }

    /**
     * @method getGraphData
     */
    getGraphData = () => {
        const { poll } = this.state;

        // Ranking poll: compute points per-answer
        if (poll.type === 'ranking') {
            // compute points per-answer (prefer answer.points from API)
            const pointsArr = poll.answers.map(a => Number(a.points || 0));

            // determine total submissions:
            // try poll.submission_count, else derive from position 1 counts
            let totalSubmissions = poll.submission_count || 0;
            if (!totalSubmissions) {
                // sum all first-position counts across answers (should equal submissions)
                let firstPosTotal = 0;
                poll.answers.forEach(a => {
                    const pos = a.position_counts || a.rank_counts || null;
                    if (pos) {
                        if (Array.isArray(pos)) {
                            firstPosTotal += Number(pos[0]) || 0;
                        } else {
                            firstPosTotal += Number(pos['1']) || Number(pos[1]) || 0;
                        }
                    }
                });
                totalSubmissions = firstPosTotal;
            }

            // max points per submission = sum of points for positions 1..min(answers.length)
            const numAnswers = poll.answers.length || 0;
            let maxPointsPerSubmission = 0;
            for (let i = 0; i < numAnswers; i++) {
                // position i (0-based) gets numAnswers - i points
                maxPointsPerSubmission += Math.max(0, numAnswers - i);
            }

            // compute percent of max possible. If we can't compute totalSubmissions, fallback to normalizing by max observed points.
            let normalized = [];
            const maxObserved = Math.max(...pointsArr, 0) || 1;
            if (totalSubmissions > 0 && maxPointsPerSubmission > 0) {
                normalized = pointsArr.map(pt => pt / (maxPointsPerSubmission * totalSubmissions));
            } else {
                normalized = pointsArr.map(pt => pt / maxObserved);
            }

            return {
                labels: poll.answers.map(a => {
                    let answer = a.answer.substring(0, 47);
                    if (answer !== a.answer) {
                        answer += '...';
                    }
                    return this.wordWrap(answer, { width: 25 })
                }),
                datasets: [
                    {
                        label: 'Ranking points (higher is better)',
                        data: normalized,
                        borderWidth: 1,
                        // Use white for all bars/borders
                        // Use a colour per bar for ranking so each bar line/fill is unique
                        backgroundColor: poll.answers.map((_, i) => modernColors[i % modernColors.length]),
                        borderColor: poll.answers.map((_, i) => modernColors[i % modernColors.length]),
                        borderRadius: { topRight: 12, bottomRight: 12, topLeft: 0, bottomLeft: 0 },
                    },
                ],
            };
        }

        // Non-ranking poll: existing behaviour
        const total = poll.answers.reduce((acc, answer) => acc + answer.submission_count, 0) || 1;

        return {
            labels: poll.answers.map(a => {
                let answer = a.answer.substring(0, 47);
                if (answer !== a.answer) {
                    answer += '...';
                }

                return this.wordWrap(answer, { width: 25 })
            }),
            datasets: [
                {
                    label: '% of Votes',
                    data: poll.answers.map(a => (a.submission_count / total)),
                    borderWidth: 0,
                    // Use white for all bars
                    backgroundColor: poll.answers.map((_, i) => modernColors[i % modernColors.length]),                    borderRadius: { topRight: 12, bottomRight: 12, topLeft: 0, bottomLeft: 0 },
                    barPercentage: 0.7,
                    categoryPercentage: 0.7,
                    // Add shadow effect via plugin (see below)
                },
            ],
        }
    };

    /**
     * @method getTitle
     */
    getTitle = () => {
        const { poll } = this.state;

        return (
            {
                plugins: {
                    title: {
                        display: true,
                        text: poll.question,
                        color: '#ffffff',
                        font: {
                            size: 18,
                        },
                    },
                },
            }
        );
    }

    /**
     * @method getTitle
     * @param {string} str
     * @param {object} options
     * @return {array}
     */
    wordWrap = (str, options) => {
        options = options || {};

        if (str == null) {
            return str;
        }

        const width = options.width || 50;

        let regexString = '.{1,' + width + '}';

        if (options.cut !== true) {
            regexString += '([\\s\u200B]+|$)|[^\\s\u200B]+?([\\s\u200B]+|$)';
        }

        const re = new RegExp(regexString, 'g');

        return str.match(re) || [];
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const { loading, poll } = this.state;

        // Compute graph data once and derive legend colours from the dataset to ensure exact match
        let legend = null;
        const graphData = (!loading && poll) ? this.getGraphData() : null;
        if (graphData && poll) {
            const total = poll.answers.reduce((acc, answer) => acc + answer.submission_count, 0);
            // support dataset backgroundColor being either a single string or an array
            const bg = graphData.datasets?.[0]?.backgroundColor || [];
            const colorArray = Array.isArray(bg) ? bg : [bg];

            // Show a legend derived from the dataset colours (works for both ranking and non-ranking)
            if(poll.type !== "ranking") {
                legend = (
                    <div className="flex flex-row justify-center items-center space-x-6">
                        {poll.answers.map((a, i) => {
                            const percent = total > 0 ? ((a.submission_count / total) * 100).toFixed(0) : 0;
                            // fallback to white if no color present
                            const color = colorArray[i % colorArray.length] || modernColors[i % modernColors.length];
                            return (
                                <div key={"answer-"+a.id} className="flex items-center space-x-2">
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 16,
                                        height: 16,
                                        borderRadius: 4,
                                        backgroundColor: color,
                                    }}
                                />
                                    <span className="text-white font-bold">{percent}%</span>
                                </div>
                            );
                        })}
                    </div>
                );
            }
        }

        return (
            <div className="pb-3">
                {(loading || !poll) && (<Loading />)}

                {!loading && poll &&
                    <>
                        <div
                            key={"poll-"+poll.id}
                            className="mx-auto text-white bg-transparent text-xl mt-6 mb-3 rounded-xl shadow-lg text-center"
                            style={{ boxShadow: '0 4px 24px 0 rgba(60,72,100,0.08)' }}
                        >
                            <h1 className={"text-white text-xl font-bold text-center"}>
                                {poll.question}
                            </h1>
                            <div className="px-4 pb-2">
                                <div className={`p-3`}>
                                    <Bar
                                        data={graphData}
                                        options={{
                                            ...options,
                                            plugins: {
                                                ...options.plugins,
                                                datalabels: {
                                                    ...options.plugins.datalabels,
                                                    display: true,
                                                    anchor: 'end',
                                                    align: 'right',
                                                    offset: 12,
                                                    color: '#ffffff',
                                                    font: {
                                                        weight: 'bold',
                                                        size: 16,
                                                    },
                                                    formatter: (value) => (value * 100).toFixed(0) + '%',
                                                },
                                            },
                                        }}
                                        height={400}
                                        plugins={[
                                            {
                                                // Add subtle shadow to bars
                                                beforeDraw: (chart) => {
                                                    const ctx = chart.ctx;
                                                    ctx.save();
                                                    chart.getDatasetMeta(0).data.forEach(bar => {
                                                        ctx.shadowColor = 'rgba(60,72,100,0.10)';
                                                        ctx.shadowBlur = 8;
                                                        ctx.shadowOffsetX = 2;
                                                        ctx.shadowOffsetY = 4;
                                                    });
                                                    ctx.restore();
                                                }
                                            }
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            key={"legend-"+poll.id}
                            className="max-w-2xl mx-auto text-white bg-transparent text-xl mt-6 mb-3 rounded-xl shadow-lg text-center"
                            style={{ boxShadow: '0 4px 24px 0 rgba(60,72,100,0.08)' }}
                        >

                        {/* Legend card under the graph */}
                            {legend &&
                                <div className="bg-[#23263a] rounded-xl shadow-lg mt-4 py-4 px-6">
                                    {legend}
                                </div>
                            }
                        </div>
                    </>
                }
            </div>
        );
    }
}
